import React from 'react'
import { ScrollReveal } from './ScrollReveal'

interface VideoSectionItem {
  eyebrow: string
  title: string
  description: string
  bullets: string[]
  videoSrc: string
  reverse?: boolean
  panelClass: string
  borderClass: string
  titleClass: string
}

interface TextSectionItem {
  eyebrow: string
  title: string
  description: string
  bullets: string[]
  panelClass: string
  borderClass: string
  titleClass: string
}

interface PlaceholderSectionItem extends TextSectionItem {
  placeholder: string
  accentTextClass: string
  accentBarClass: string
  reverse?: boolean
}

const customToolsSection: VideoSectionItem = {
  eyebrow: '[ Programmable Surface ]',
  title: 'Build Your Own Tools.',
  description:
    "Graviton ships with its own tools, but the interface itself is programmable. Users can build custom apps with UI or let agents run them headlessly inside the same workspace. If you don't like the default browser, replace it.",
  bullets: ['UI + headless', 'Custom browsers', 'Apps inside chat'],
  videoSrc: '/video/custom_app_2x.mp4',
  panelClass: 'bg-white dark:bg-black',
  borderClass: 'border-zinc-200 dark:border-zinc-900',
  titleClass: 'text-[#0052FF]',
}

const skillsSection: TextSectionItem = {
  eyebrow: '[ Extensibility Layer ]',
  title: 'Skills. MCPs. Subagents.',
  description:
    'Graviton is built to load specialized behavior and external systems. Skills provide reusable workflows, MCPs connect new capabilities, and subagents let work split into focused continuations instead of one overloaded thread.',
  bullets: ['Skills', 'MCP support', 'Subagents'],
  panelClass: 'bg-white dark:bg-black',
  borderClass: 'border-zinc-200 dark:border-zinc-900',
  titleClass: 'text-red-600 dark:text-red-400',
}

const hooksSection: TextSectionItem = {
  eyebrow: '[ Flow Control ]',
  title: 'Hooks That Shape Memory.',
  description:
    'Users can intercept chat flow before or after a turn, editing prompts, injecting context, or automating structure. This is how Graviton can maintain live branch summaries and keep evolving memory readable over time.',
  bullets: ['Pre-send edits', 'Auto summaries', 'Memory builder hooks'],
  panelClass: 'bg-zinc-50 dark:bg-zinc-950',
  borderClass: 'border-zinc-200 dark:border-zinc-800',
  titleClass: 'text-emerald-600 dark:text-emerald-400',
}

const themesSection: VideoSectionItem = {
  eyebrow: '[ Interface As Output ]',
  title: 'Ask Graviton To Theme Itself.',
  description:
    'The workspace is themeable end to end. Ask Graviton to make you a theme and it can restyle the interface itself, turning the product surface into something users can shape as easily as the work happening inside it.',
  bullets: ['AI-made themes', 'Whole UI restyling', 'Personal workflow surface'],
  videoSrc: '/video/themes.mp4',
  panelClass: 'bg-zinc-50 dark:bg-zinc-950',
  borderClass: 'border-zinc-200 dark:border-zinc-800',
  titleClass: 'text-violet-600 dark:text-violet-400',
  reverse: true,
}

const remoteSection: PlaceholderSectionItem = {
  eyebrow: '[ Everywhere ]',
  title: 'Remote, Open, And Yours.',
  description:
    'Control Graviton remotely from any device with a browser — your phone, TV, tablet, or even a fridge. And because it is open source, the surface can evolve with the people using it, not only the defaults it shipped with.',
  bullets: ['Remote access', 'Any browser device', 'Open source'],
  placeholder: 'REMOTE ACCESS / DEVICE WALL',
  accentTextClass: 'text-[#0052FF]',
  accentBarClass: 'bg-[#0052FF]',
  panelClass: 'bg-white dark:bg-black',
  borderClass: 'border-zinc-200 dark:border-zinc-900',
  titleClass: 'text-[#0052FF]',
}

const terminalSection: TextSectionItem = {
  eyebrow: '[ Built In Workspace ]',
  title: 'Terminal Inside The App.',
  description:
    'Run commands without leaving Graviton. The built-in terminal keeps shell access next to your chat, tools, and project context so you can inspect files, build, test, and ship changes in one flow.',
  bullets: ['Built-in terminal', 'Run builds and tests', 'Stay in context'],
  panelClass: 'bg-zinc-50 dark:bg-zinc-950',
  borderClass: 'border-zinc-200 dark:border-zinc-800',
  titleClass: 'text-amber-600 dark:text-amber-400',
}

const browserSection: TextSectionItem = {
  eyebrow: '[ Frontend Ready ]',
  title: 'Built-In Browser + DevTools.',
  description:
    'Preview web apps directly inside Graviton with an integrated browser and developer tools. Front-end developers can inspect layouts, debug styling, trace interactions, and iterate on UI without breaking the workflow between code, chat, and preview.',
  bullets: ['Embedded browser', 'DevTools included', 'Front-end workflow'],
  panelClass: 'bg-white dark:bg-black',
  borderClass: 'border-zinc-200 dark:border-zinc-900',
  titleClass: 'text-cyan-600 dark:text-cyan-400',
}

const TextCard: React.FC<{ section: TextSectionItem; delay?: string }> = ({ section, delay = '' }) => (
  <ScrollReveal
    delay={delay}
    className={`border-2 ${section.borderClass} ${section.panelClass} transition-colors duration-500`}
  >
    <div className='p-8 md:p-10 h-full'>
      <span className='mono text-[11px] md:text-xs font-bold tracking-[0.28em] uppercase text-zinc-400'>
        {section.eyebrow}
      </span>
      <h3 className={`mt-4 text-3xl md:text-5xl font-black tracking-tighter ${section.titleClass}`}>
        {section.title}
      </h3>
      <p className='mt-6 text-lg leading-relaxed text-zinc-600 dark:text-zinc-400'>
        {section.description}
      </p>

      <div className='mt-8 flex flex-wrap gap-2'>
        {section.bullets.map(bullet => (
          <span
            key={bullet}
            className='border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400'
          >
            {bullet}
          </span>
        ))}
      </div>
    </div>
  </ScrollReveal>
)

const VideoCard: React.FC<{ section: VideoSectionItem; delay?: string }> = ({ section, delay = '' }) => {
  const mediaOrderClass = section.reverse ? 'lg:order-1' : 'lg:order-2'
  const copyOrderClass = section.reverse ? 'lg:order-2' : 'lg:order-1'
  const mediaBorderClass = section.reverse
    ? 'border-b lg:border-b-0 lg:border-r border-zinc-200 dark:border-zinc-800'
    : 'border-b lg:border-b-0 lg:border-l border-zinc-200 dark:border-zinc-800'

  return (
    <ScrollReveal
      delay={delay}
      className={`group overflow-hidden border-2 ${section.borderClass} ${section.panelClass} transition-colors duration-500`}
    >
      <div className='grid grid-cols-1 lg:grid-cols-12'>
        <div className={`lg:col-span-5 p-8 md:p-10 flex flex-col justify-between ${copyOrderClass}`}>
          <div>
            <span className='mono text-[11px] md:text-xs font-bold tracking-[0.28em] uppercase text-zinc-400'>
              {section.eyebrow}
            </span>
            <h3 className={`mt-4 text-3xl md:text-5xl font-black tracking-tighter ${section.titleClass}`}>
              {section.title}
            </h3>
            <p className='mt-6 text-lg leading-relaxed text-zinc-600 dark:text-zinc-400'>
              {section.description}
            </p>
          </div>

          <div className='mt-8 flex flex-wrap gap-2'>
            {section.bullets.map(bullet => (
              <span
                key={bullet}
                className='border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400'
              >
                {bullet}
              </span>
            ))}
          </div>
        </div>

        <div className={`lg:col-span-7 ${mediaOrderClass} ${mediaBorderClass}`}>
          <div className='p-6 md:p-8 h-full'>
            <div className='relative min-h-[280px] md:min-h-[360px] h-full bg-zinc-950 dark:bg-black overflow-hidden'>
              <video
                className='w-full h-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.04]'
                autoPlay
                loop
                muted
                playsInline
                preload='metadata'
              >
                <source src={section.videoSrc} type='video/mp4' />
              </video>
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  )
}

const PlaceholderCard: React.FC<{ section: PlaceholderSectionItem; delay?: string }> = ({ section, delay = '' }) => {
  const mediaOrderClass = section.reverse ? 'lg:order-1' : 'lg:order-2'
  const copyOrderClass = section.reverse ? 'lg:order-2' : 'lg:order-1'
  const mediaBorderClass = section.reverse
    ? 'border-b lg:border-b-0 lg:border-r border-zinc-200 dark:border-zinc-800'
    : 'border-b lg:border-b-0 lg:border-l border-zinc-200 dark:border-zinc-800'

  return (
    <ScrollReveal
      delay={delay}
      className={`overflow-hidden border-2 ${section.borderClass} ${section.panelClass} transition-colors duration-500`}
    >
      <div className='grid grid-cols-1 lg:grid-cols-12'>
        <div className={`lg:col-span-5 p-8 md:p-10 flex flex-col justify-between ${copyOrderClass}`}>
          <div>
            <span className='mono text-[11px] md:text-xs font-bold tracking-[0.28em] uppercase text-zinc-400'>
              {section.eyebrow}
            </span>
            <h3 className={`mt-4 text-3xl md:text-5xl font-black tracking-tighter ${section.titleClass}`}>
              {section.title}
            </h3>
            <p className='mt-6 text-lg leading-relaxed text-zinc-600 dark:text-zinc-400'>
              {section.description}
            </p>
          </div>

          <div className='mt-8 flex flex-wrap gap-2'>
            {section.bullets.map(bullet => (
              <span
                key={bullet}
                className='border border-zinc-200 dark:border-zinc-800 px-3 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400'
              >
                {bullet}
              </span>
            ))}
          </div>
        </div>

        <div className={`lg:col-span-7 ${mediaOrderClass} ${mediaBorderClass}`}>
          <div className='p-6 md:p-8 h-full'>
            <div className='relative min-h-[280px] md:min-h-[360px] h-full border border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-100/80 dark:bg-zinc-900/60 overflow-hidden'>
              <div className='absolute top-0 left-0 right-0 h-12 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between px-4 z-20'>
                <div className='flex items-center gap-2'>
                  <div className='w-2.5 h-2.5 bg-[#0052FF]' />
                  <div className='w-2.5 h-2.5 bg-red-500' />
                  <div className='w-2.5 h-2.5 bg-emerald-500' />
                </div>
                <span className='mono text-[10px] uppercase tracking-[0.3em] text-zinc-400'>Media Placeholder</span>
              </div>

              <div className='absolute inset-0 pointer-events-none opacity-[0.05] dark:opacity-[0.04] bg-[radial-gradient(#000_1px,transparent_1px)] dark:bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:18px_18px]' />

              <div className='relative h-full flex items-center justify-center px-6 pt-12 pb-6 text-center'>
                <div>
                  <div className={`w-16 h-1 mx-auto mb-5 ${section.accentBarClass}`} />
                  <p className='mono text-[10px] uppercase tracking-[0.35em] text-zinc-400'>Animation / Video</p>
                  <h4 className={`mt-3 text-2xl md:text-4xl font-black tracking-tighter ${section.accentTextClass}`}>
                    {section.placeholder}
                  </h4>
                  <p className='mt-4 max-w-md text-sm md:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed'>
                    Still recording a video for this.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  )
}

export const PlatformSections: React.FC = () => {
  return (
    <div className='space-y-8'>
      <VideoCard section={customToolsSection} />

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        <TextCard section={skillsSection} />
        <TextCard section={hooksSection} delay='reveal-delayed-1' />
      </div>

      <VideoCard section={themesSection} />

      <PlaceholderCard section={remoteSection} />

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        <TextCard section={terminalSection} />
        <TextCard section={browserSection} delay='reveal-delayed-1' />
      </div>
    </div>
  )
}
